// A room's RF sensors, and their state, live.
//
// ⚠️ AN RF SENSOR IS NOT A DEVICE ROW, so no room query will ever return it.
// A door, safe, fire or motion sensor is a bare transmitter with no unique
// number (docs/17); it lives in `RfDevice`, carries its own RoomID, and has to
// be fetched alongside the room's devices or it simply never appears — which is
// exactly what a customer reported: the receiver was in the room, the safe
// locker was not, and the state they wanted was in the database all along.
//
// Two sources, in this order:
//   SERVER   the state as it was saved — right on first paint, survives a
//            restart, and is what every other phone sees.
//   MQTT     `[<receiver>]RfV(<code>,...)` as it happens, so the tile flips
//            while the customer is looking at it. The server persists the same
//            press in parallel; this is the same fact arriving sooner, never a
//            second source of truth.
//
// ⚠️ THE APP NEVER ACTS ON `RfV`. The server publishes any mapped actions. This
// only moves a label on screen.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRfDevices } from '../api/api';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../mqtt/mqttClient';
import { parseRfV, transmitterOf } from '../devices/rfCodes';
import { SERVER_TZ_OFFSET_MS } from '../devices/deviceStatus';

// ⚠️ A LIVE TIME MUST BE WRITTEN IN THE SERVER'S FORMAT — IST labelled as UTC
// (deviceStatus.js) — because every reader (rfAgo → parseServerTime) takes the
// offset back off. A real-UTC ISO string here made a sensor that had just fired
// read "5 h ago", grey, on the phone.
const serverStamp = (ms) => new Date(ms + SERVER_TZ_OFFSET_MS).toISOString();

// The same window the backend and the learn panel use: a transmitter repeats
// every frame 4-8 times per press, and a tile that re-rendered per frame would
// flicker through one event six times.
const REPEAT_MS = 3000;

/**
 * @param roomId  the room whose sensors to show
 * @param opts.locationId  instead of a room: every named, active sensor of the
 *                 location (the desktop shell draws the whole location at once)
 * @param opts.onEvent  (sensor, meaning) once per real event (repeats collapsed)
 *                 for a code that belongs to one of these sensors — the desktop
 *                 rings its bell on it
 * @param opts.viewerUserId  who is looking — a sensor whose access was taken
 *                 from them (RfDeviceMute.NoAccess) is left out by the server
 * -> { sensors, reload, loading }
 *    Each sensor carries LastEvent / LastAt, updated live.
 */
export default function useRfSensors(roomId, { locationId, onEvent, viewerUserId } = {}) {
  const [sensors, setSensors] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastSeen = useRef(new Map());
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const sensorsRef = useRef(sensors);
  sensorsRef.current = sensors;

  const reload = useCallback(async () => {
    const query = roomId ? { RoomID: roomId } : (locationId ? { LocationID: locationId } : null);
    if (!query) { setSensors([]); return; }
    if (viewerUserId) query.ViewerUserId = viewerUserId;
    setLoading(true);
    try {
      const res = await getRfDevices(query);
      const list = Array.isArray(res.data) ? res.data : [];
      // The room query is already named + active on the server; the location one is not.
      setSensors(roomId ? list : list.filter((d) => String(d.Name || '').trim() && Number(d.Active) === 1));
    } catch (e) {
      // A room must still draw its devices when the RF endpoint is absent (an
      // older backend) or unreachable. No sensors is the honest fallback.
      setSensors([]);
    } finally {
      setLoading(false);
    }
  }, [roomId, locationId, viewerUserId]);

  useEffect(() => { reload(); }, [reload]);

  // code -> { rfDeviceId, meaning }, so an arriving code names its own event
  // without another request.
  const byCode = useMemo(() => {
    const m = new Map();
    sensors.forEach((d) => (d.Codes || []).forEach((c) => {
      m.set(String(c.REMOTEVALUE), {
        rfDeviceId: d.RfDeviceId,
        meaning: String(c.DeviceAction || '').trim(),
      });
    }));
    return m;
  }, [sensors]);

  // Which receivers to listen to. A sensor can be heard by more than one, so
  // this is the set of every receiver that has reported one of these sensors.
  const receivers = useMemo(() => [...new Set(
    sensors.map((d) => String(d.ReceiverUniqueNumber || '').trim()).filter(Boolean),
  )], [sensors]);

  useEffect(() => {
    if (!receivers.length) return undefined;
    const topics = receivers.map((u) => `${u}/client`);
    topics.forEach(subscribeTopic);
    const off = addMessageListener((topic, payload) => {
      if (!topics.includes(topic)) return;
      const hit = parseRfV(payload);
      if (!hit) return;
      const now = Date.now();
      const prev = lastSeen.current.get(hit.code);
      lastSeen.current.set(hit.code, now);
      if (prev != null && now - prev < REPEAT_MS) return;   // the repeat burst

      const known = byCode.get(String(hit.code));
      const transmitter = transmitterOf(hit.code);
      setSensors((list) => list.map((d) => {
        const mine = known
          ? d.RfDeviceId === known.rfDeviceId
          : Number(d.TransmitterId) === transmitter;
        if (!mine) return d;
        return {
          ...d,
          // An unnamed code still moves the time: the sensor was heard from,
          // even if nobody has said what that code means yet.
          LastEvent: known && known.meaning ? known.meaning : d.LastEvent,
          LastCode: hit.code,
          LastAt: serverStamp(now),
          liveAt: now,
        };
      }));
      if (known && onEventRef.current) {
        const sensor = sensorsRef.current.find((d) => d.RfDeviceId === known.rfDeviceId);
        if (sensor) onEventRef.current(sensor, known.meaning, String(hit.code));
      }
    });
    return () => {
      off();
      topics.forEach(unsubscribeTopic);
    };
  }, [receivers, byCode]);

  return { sensors, reload, loading };
}
